import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import IndustrySelect from "@/components/IndustrySelect";
import ContactsEditor from "@/components/ContactsEditor";
import ActivityLog from "@/components/ActivityLog";
import DraftBanner from "@/components/DraftBanner";
import VisibilityToggle from "@/components/VisibilityToggle";
import { useDraftGuard } from "@/hooks/useDraftGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save, Loader2, FileText, Mail, History as HistoryIcon, Layers } from "lucide-react";
import { toast } from "sonner";

const stageColor = (s) => ({
  new: "bg-slate-100 text-slate-600", quoting: "bg-[#E6ECF5] text-[#1B4383]",
  quoted: "bg-amber-100 text-amber-700", won: "bg-emerald-100 text-emerald-700",
  lost: "bg-rose-100 text-rose-700",
}[s] || "bg-slate-100 text-slate-600");

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }); } catch { return iso || ""; } };

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromRfq = location.state?.fromRfq;
  const fromIndustry = location.state?.fromIndustry;
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(null);
  const [history, setHistory] = useState({ rfqs: [], emails: [], revisions: [] });
  const [saving, setSaving] = useState(false);
  const [activityKey, setActivityKey] = useState(0);
  const [serverContacts, setServerContacts] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${id}`);
      setForm({ ...data, contacts: data.contacts || [], notes: data.notes || "" });
      setServerContacts((data.contacts || []).length);
      api.get(`/clients/${id}/history`).then((r) => setHistory(r.data)).catch(() => {});
    } catch (e) {
      toast.error("This client is private — you don't have access to its profile.");
      navigate(fromRfq ? `/rfqs/${fromRfq.id}` : "/clients");
    }
  }, [id, navigate, fromRfq]);
  useEffect(() => { load(); }, [load]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    const nowCount = (form.contacts || []).length;
    let clear_contacts = false;
    if (nowCount === 0 && serverContacts > 0) {
      if (!window.confirm(`This will remove all ${serverContacts} saved contact(s) for this client. Continue?`)) return;
      clear_contacts = true;
    }
    setSaving(true);
    try {
      const primary = (form.contacts || []).find((c) => c.primary) || (form.contacts || [])[0] || {};
      await api.put(`/clients/${id}`, {
        name: form.name, industry: form.industry, short_name: (form.short_name || "").trim(),
        contact_person: primary.name || "", contact_email: primary.email || "",
        phone: form.phone || "", website: form.website || "", tin: form.tin || "", vrn: form.vrn || "", address: form.address || "",
        preferred_delivery_point: form.preferred_delivery_point || "",
        notes: form.notes || "", contacts: form.contacts || [], visibility: form.visibility || "team", clear_contacts,
      });
      toast.success("Client saved");
      guard.markSaved();
      setActivityKey((k) => k + 1);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const editable = form ? {
    name: form.name, industry: form.industry, short_name: (form.short_name || "").trim(), phone: form.phone || "", website: form.website || "",
    tin: form.tin || "", vrn: form.vrn || "", address: form.address || "", preferred_delivery_point: form.preferred_delivery_point || "", notes: form.notes || "",
    contacts: form.contacts || [], visibility: form.visibility || "team",
  } : null;
  const guard = useDraftGuard(`client:${id}`, editable, { enabled: !!form });
  const restoreDraft = () => { const d = guard.restore(); if (d) setForm((f) => ({ ...f, ...d })); };

  if (!form) return <AppLayout title="Client"><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></AppLayout>;

  return (
    <AppLayout title="Client Detail">
      {fromRfq ? (
        <button onClick={() => navigate(`/rfqs/${fromRfq.id}`)} className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-rfq">
          <ArrowLeft className="h-4 w-4" /> Back to RFQ{fromRfq.title ? ` — ${fromRfq.title}` : ""}
        </button>
      ) : fromIndustry ? (
        <button onClick={() => navigate(`/industries/${fromIndustry.value}`)} className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-industry">
          <ArrowLeft className="h-4 w-4" /> Back to {fromIndustry.label || "Industry"}
        </button>
      ) : (
        <button onClick={() => navigate("/clients")} className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-clients">
          <ArrowLeft className="h-4 w-4" /> Back to Clients
        </button>
      )}

      {guard.recovered && <DraftBanner onRestore={restoreDraft} onDiscard={guard.discard} />}

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <div className="text-xs font-semibold tracking-wide text-brand-soft">Client</div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="client-detail-name">{form.name}</h2>
          {form.website && (
            <a href={form.website.startsWith("http") ? form.website : `https://${form.website}`} target="_blank" rel="noreferrer"
              className="mt-1 inline-block text-sm text-[#1B4383] hover:underline" data-testid="client-detail-website-link">{form.website}</a>
          )}
        </div>
        <Button onClick={save} disabled={saving} data-testid="client-save-changes" className="w-full sm:w-auto">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save Changes
        </Button>
      </div>

      {isAdmin && (
        <div className="mb-6">
          <VisibilityToggle value={form.visibility || "team"} onChange={(v) => set({ visibility: v })} />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-lg border border-border bg-white p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Company Name</Label>
                <Input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="client-detail-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Industry</Label>
                <IndustrySelect value={form.industry} onChange={(v) => set({ industry: v })} testid="client-detail-industry" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Folder Short Name</Label>
                <Input value={form.short_name || ""} onChange={(e) => set({ short_name: e.target.value })} placeholder="e.g. SWALA" data-testid="client-detail-short-name" />
                <p className="text-[11px] text-muted-foreground">Used In Offline RFQ Folder Names Instead Of The Full Company Name.</p>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Phone</Label>
                <Input value={form.phone || ""} onChange={(e) => set({ phone: e.target.value })} data-testid="client-detail-phone" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Website</Label>
                <Input placeholder="https://example.com" value={form.website || ""} onChange={(e) => set({ website: e.target.value })} data-testid="client-detail-website" /></div>
              <div className="space-y-1.5"><Label className="text-xs">TIN</Label>
                <Input value={form.tin || ""} onChange={(e) => set({ tin: e.target.value })} data-testid="client-detail-tin" /></div>
              <div className="space-y-1.5"><Label className="text-xs">VRN</Label>
                <Input value={form.vrn || ""} onChange={(e) => set({ vrn: e.target.value })} data-testid="client-detail-vrn" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Address</Label>
                <Input value={form.address || ""} onChange={(e) => set({ address: e.target.value })} data-testid="client-detail-address" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Preferred Delivery Point</Label>
                <Input placeholder="e.g. Site warehouse, Dar es Salaam port, mine gate…" value={form.preferred_delivery_point || ""} onChange={(e) => set({ preferred_delivery_point: e.target.value })} className={`${!form.preferred_delivery_point ? "bg-[#FFFBEB] border-[#EAD9A0] focus-visible:ring-[#E0B84B]" : ""}`} data-testid="client-detail-delivery-point" />
                <p className="text-[11px] text-muted-foreground">Where we usually deliver this client's orders — pre-fills logistics &amp; delivery costing.</p></div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Notes</Label>
                <Textarea rows={3} placeholder="Account notes, terms, history…" value={form.notes || ""} onChange={(e) => set({ notes: e.target.value })} data-testid="client-detail-notes" /></div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-white p-6">
            <h3 className="mb-4 font-heading text-sm font-bold tracking-tight">Contact Persons</h3>
            <ContactsEditor contacts={form.contacts} company={form.name} onChange={(c) => set({ contacts: c })} />
          </div>
        </div>

        <div className="rounded-lg border border-border bg-white p-6" data-testid="client-360-history">
          <div className="mb-4 flex items-center gap-2">
            <HistoryIcon className="h-4 w-4 text-[#1B4383]" />
            <h3 className="font-heading text-sm font-bold tracking-tight">360° History</h3>
          </div>

          <div className="mb-2 flex items-center gap-2 text-xs font-semibold tracking-wide text-slate-400"><FileText className="h-3.5 w-3.5" />Opportunities</div>
          <div className="space-y-2">
            {history.rfqs.length ? history.rfqs.map((r) => (
              <Link to={`/rfqs/${r.id}`} key={r.id} className="block rounded-md border border-border p-2.5 hover:bg-slate-50" data-testid={`history-rfq-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-[#1B4383]">{r.code || "—"}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${stageColor(r.stage)}`}>{r.stage}</span>
                </div>
                <div className="mt-1 truncate text-sm text-slate-800">{r.title}</div>
              </Link>
            )) : <p className="text-xs text-muted-foreground">No opportunities yet.</p>}
          </div>

          <div className="mb-2 mt-5 flex items-center gap-2 text-xs font-semibold tracking-wide text-slate-400"><Mail className="h-3.5 w-3.5" />Quotes Emailed</div>
          <div className="space-y-2">
            {history.emails.length ? history.emails.map((e) => (
              <div key={e.id} className="rounded-md border border-border p-2.5" data-testid={`history-email-${e.id}`}>
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="font-medium text-slate-800">{e.quote_no}</span>
                  <span className="text-xs text-muted-foreground">{fmt(e.at)}</span>
                </div>
                <div className="truncate text-xs text-muted-foreground">to {e.to} · {e.sent_by_name || "—"}</div>
              </div>
            )) : <p className="text-xs text-muted-foreground">No quotes emailed yet.</p>}
          </div>

          <div className="mb-2 mt-5 flex items-center gap-2 text-xs font-semibold tracking-wide text-slate-400"><Layers className="h-3.5 w-3.5" />Revisions</div>
          <div className="space-y-2">
            {history.revisions.length ? history.revisions.map((v) => (
              <div key={v.id} className="rounded-md border border-border p-2.5 text-sm" data-testid={`history-rev-${v.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-800">{v.label}</span>
                  <span className="text-xs text-muted-foreground">{fmt(v.created_at)}</span>
                </div>
              </div>
            )) : <p className="text-xs text-muted-foreground">No revisions yet.</p>}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <ActivityLog entityType="clients" entityId={id} refreshKey={activityKey} />
      </div>
    </AppLayout>
  );
}
